(() => {
  class KixboxOutfitTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.tabs = Array.from(this.querySelectorAll("[data-outfit-tab]"));
      this.panels = Array.from(this.querySelectorAll("[data-outfit-panel]"));
      this.initializeSizePickers();
      this.addEventListener("click", (event) => this.onClick(event));
      this.addEventListener("submit", (event) => {
        if (event.target.matches("[data-outfit-direct-form]")) this.emitEngagement(event.target);
      });
      this.addEventListener("keydown", (event) => this.onKeydown(event));
      this.boundOutsideClick = (event) => {
        if (!this.contains(event.target)) this.closeSizePickers();
      };
      document.addEventListener("click", this.boundOutsideClick);
      this.installLauncher();
    }

    disconnectedCallback() {
      if (this.boundOutsideClick) document.removeEventListener("click", this.boundOutsideClick);
    }

    initializeSizePickers() {
      const sizeOrder = window.KixboxOutfitSizeOrder;
      if (!sizeOrder) return;
      this.querySelectorAll("[data-outfit-size-picker]").forEach((picker) => {
        if (picker.dataset.sizePickerReady === "true") return;
        const native = picker.querySelector("[data-outfit-size-native]");
        const menu = picker.querySelector("[data-outfit-size-menu]");
        if (!native || !menu) return;
        const sortNodes = (nodes) => Array.from(nodes)
          .map((node, index) => ({ node, index }))
          .sort((left, right) => sizeOrder.compareLabels(
            left.node.dataset.sizeLabel,
            right.node.dataset.sizeLabel,
          ) || left.index - right.index)
          .forEach(({ node }) => node.parentElement.append(node));
        sortNodes(native.options);
        sortNodes(menu.querySelectorAll("[data-outfit-size-option]"));
        picker.dataset.sizePickerReady = "true";
        this.syncSizePicker(picker);
      });
    }

    syncSizePicker(picker) {
      const native = picker.querySelector("[data-outfit-size-native]");
      const value = picker.querySelector("[data-outfit-size-value]");
      if (!native || !value) return;
      const selected = native.selectedOptions[0];
      value.textContent = selected?.dataset.sizeLabel || selected?.textContent?.trim() || "";
      picker.querySelectorAll("[data-outfit-size-option]").forEach((option) => {
        option.setAttribute("aria-selected", String(option.dataset.variantId === native.value));
      });
    }

    setSizePickerOpen(picker, open, focusTarget = "") {
      const trigger = picker.querySelector("[data-outfit-size-trigger]");
      const menu = picker.querySelector("[data-outfit-size-menu]");
      if (!trigger || !menu) return;
      this.closeSizePickers(picker);
      trigger.setAttribute("aria-expanded", String(open));
      menu.hidden = !open;
      if (!open || !focusTarget) return;
      const options = Array.from(menu.querySelectorAll("[data-outfit-size-option]"));
      const target = focusTarget === "last"
        ? options.at(-1)
        : options.find((option) => option.getAttribute("aria-selected") === "true") || options[0];
      target?.focus();
    }

    closeSizePickers(except = null) {
      this.querySelectorAll("[data-outfit-size-picker]").forEach((picker) => {
        if (picker === except) return;
        picker.querySelector("[data-outfit-size-trigger]")?.setAttribute("aria-expanded", "false");
        const menu = picker.querySelector("[data-outfit-size-menu]");
        if (menu) menu.hidden = true;
      });
    }

    selectSizeOption(option) {
      const picker = option.closest("[data-outfit-size-picker]");
      const native = picker?.querySelector("[data-outfit-size-native]");
      if (!picker || !native) return;
      native.value = option.dataset.variantId;
      native.dispatchEvent(new Event("change", { bubbles: true }));
      this.syncSizePicker(picker);
      this.setSizePickerOpen(picker, false);
      picker.querySelector("[data-outfit-size-trigger]")?.focus();
    }

    emit(name, element = this) {
      document.dispatchEvent(new CustomEvent("kixbox:outfit-event", {
        detail: { name, element },
      }));
    }

    emitEngagement(element = this) {
      if (this.engaged) return;
      this.engaged = true;
      this.emit("outfit_engagement", element);
    }

    installLauncher() {
      const template = this.querySelector("[data-outfit-launch-template]");
      const launcherSource = template?.content.firstElementChild;
      const buttons = document.querySelector(".product__info-container .product-form__buttons");
      if (!launcherSource || !buttons) return;

      const launcherId = `KixboxOutfitLaunch-${this.dataset.sectionId}`;
      if (document.getElementById(launcherId)) return;

      const launcher = template.content.firstElementChild.cloneNode(true);
      launcher.id = launcherId;
      launcher.setAttribute("aria-controls", this.id);
      launcher.addEventListener("click", () => {
        this.emitEngagement();
        this.emit("outfit_launcher_click");
        this.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      buttons.closest("product-form")?.insertAdjacentElement("afterend", launcher);
      this.observeLauncher(launcher);
    }

    observeLauncher(launcher) {
      const emitView = () => this.emit("outfit_widget_view");
      if (!("IntersectionObserver" in window)) {
        requestAnimationFrame(emitView);
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
        observer.disconnect();
        emitView();
      }, { threshold: 0.5 });
      observer.observe(launcher);
    }

    selectTab(tab, moveFocus = false) {
      const index = this.tabs.indexOf(tab);
      if (index < 0) return;
      this.tabs.forEach((candidate, candidateIndex) => {
        const selected = candidateIndex === index;
        candidate.setAttribute("aria-selected", String(selected));
        candidate.tabIndex = selected ? 0 : -1;
        if (moveFocus && selected) candidate.focus();
      });
      this.panels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== index;
      });
      this.emitEngagement(tab);
      this.emit("outfit_tab_select", tab);
    }

    onClick(event) {
      const engagementTarget = event.target.closest(
        "[data-outfit-tab], [data-open-similar], [data-outfit-product-link], [data-outfit-similar-product] a, [data-outfit-direct-form] [type=submit]",
      );
      if (engagementTarget && this.contains(engagementTarget)) this.emitEngagement(engagementTarget);

      const sizeTrigger = event.target.closest("[data-outfit-size-trigger]");
      if (sizeTrigger && this.contains(sizeTrigger)) {
        const picker = sizeTrigger.closest("[data-outfit-size-picker]");
        const open = sizeTrigger.getAttribute("aria-expanded") !== "true";
        if (picker) this.setSizePickerOpen(picker, open);
        return;
      }

      const sizeOption = event.target.closest("[data-outfit-size-option]");
      if (sizeOption && this.contains(sizeOption)) {
        this.selectSizeOption(sizeOption);
        return;
      }

      if (!event.target.closest("[data-outfit-size-picker]")) this.closeSizePickers();
      const tab = event.target.closest("[data-outfit-tab]");
      if (tab && this.contains(tab)) return this.selectTab(tab);

      const opener = event.target.closest("[data-open-similar]");
      if (opener && this.contains(opener)) {
        const dialog = document.getElementById(opener.dataset.dialogId);
        if (dialog && typeof dialog.showModal === "function") {
          dialog.showModal();
          this.emit("outfit_similar_open", opener);
        }
        return;
      }

      const closer = event.target.closest("[data-close-similar]");
      if (closer) closer.closest("dialog")?.close();
      if (event.target.matches("[data-outfit-similar-dialog]")) event.target.close();
    }

    onKeydown(event) {
      const sizeTrigger = event.target.closest("[data-outfit-size-trigger]");
      if (sizeTrigger && ["ArrowDown", "ArrowUp"].includes(event.key)) {
        event.preventDefault();
        const picker = sizeTrigger.closest("[data-outfit-size-picker]");
        if (picker) this.setSizePickerOpen(picker, true, event.key === "ArrowUp" ? "last" : "selected");
        return;
      }
      if (sizeTrigger && event.key === "Escape") {
        event.preventDefault();
        const picker = sizeTrigger.closest("[data-outfit-size-picker]");
        if (picker) this.setSizePickerOpen(picker, false);
        return;
      }

      const sizeOption = event.target.closest("[data-outfit-size-option]");
      if (sizeOption) {
        const picker = sizeOption.closest("[data-outfit-size-picker]");
        const options = Array.from(picker.querySelectorAll("[data-outfit-size-option]"));
        const index = options.indexOf(sizeOption);
        const offsets = { ArrowDown: 1, ArrowUp: -1 };
        if (event.key in offsets) {
          event.preventDefault();
          options[(index + offsets[event.key] + options.length) % options.length]?.focus();
          return;
        }
        if (["Home", "End"].includes(event.key)) {
          event.preventDefault();
          (event.key === "Home" ? options[0] : options.at(-1))?.focus();
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          this.setSizePickerOpen(picker, false);
          picker.querySelector("[data-outfit-size-trigger]")?.focus();
          return;
        }
      }

      const tab = event.target.closest("[data-outfit-tab]");
      if (tab) {
        const index = this.tabs.indexOf(tab);
        const direction = window.getComputedStyle(tab.parentElement).direction === "rtl" ? -1 : 1;
        const keys = { ArrowRight: direction, ArrowLeft: -direction };
        if (event.key in keys) {
          event.preventDefault();
          const next = (index + keys[event.key] + this.tabs.length) % this.tabs.length;
          this.selectTab(this.tabs[next], true);
        } else if (event.key === "Home" || event.key === "End") {
          event.preventDefault();
          this.selectTab(event.key === "Home" ? this.tabs[0] : this.tabs.at(-1), true);
        }
      }
      if (event.key === "Escape") {
        this.querySelector("dialog[open]")?.close();
      }
    }
  }

  if (!customElements.get("kixbox-outfit-tabs")) {
    customElements.define("kixbox-outfit-tabs", KixboxOutfitTabs);
  }
})();
